import * as t from 'io-ts'
import { DateFromISOString } from 'io-ts-types'
import { nullable } from 'lib/request'

import { TRoleType, TUser } from '../users/users.codecs'

export const TFilledTerms = t.strict({
  filled: t.number,
  language: t.string,
})

export type FilledTerm = t.TypeOf<typeof TFilledTerms>

export const TTerms = t.strict({
  languages: nullable(t.array(TFilledTerms)),
  total: t.number,
})

export const TPermissionType = t.union([
  t.literal('owner'),
  t.literal('collaborator'),
  t.literal('translator'),
])

export type UserPermissionType = Exclude<
  t.TypeOf<typeof TPermissionType>,
  'owner'
>

export const TMembertatus = t.union([
  t.literal('active'),
  t.literal('inactive'),
])

export type MemberStatus = t.TypeOf<typeof TMembertatus>

export const TProjectTeamMember = t.intersection([
  TUser,
  t.strict({
    status: TMembertatus,
    permission: TPermissionType,
    languages: t.array(t.string),
  }),
  t.partial({
    lastActiveAt: nullable(DateFromISOString),
    createdAt: DateFromISOString,
  }),
])

const TTeamMember = t.intersection([
  TProjectTeamMember,
  t.strict({
    role: TRoleType,
    plan: nullable(
      t.strict({
        planId: t.string,
        name: t.string,
      }),
    ),
  }),
])

export const TTeamMembersList = t.array(TTeamMember)

export type TeamMember = t.TypeOf<typeof TTeamMember>

export const TPlaceholder = t.strict({
  base: t.string,
  namedLeft: t.string,
  namedRight: t.string,
})

export type Placeholder = t.TypeOf<typeof TPlaceholder>

export const TLinkedProject = t.strict({
  projectId: t.string,
  name: t.string,
})

export const TIndent = t.strict({
  json: t.number,
  xml: t.number,
})

export type Indent = t.TypeOf<typeof TIndent>

const TProjectType = t.union([
  t.literal('standard'),
  t.literal('documentation'),
])

export type ProjectType = t.TypeOf<typeof TProjectType>

export const TProject = t.intersection([
  t.strict({
    avatarUrl: nullable(t.string),
    createdAt: DateFromISOString,
    lastUpdatedAt: DateFromISOString,
    mainLanguage: t.string,
    description: t.string,
    languages: t.array(t.string),
    name: t.string,
    projectId: t.string,
    terms: TTerms,
    team: t.array(TProjectTeamMember),
    isFavorite: t.boolean,
    placeholders: TPlaceholder,
    indent: TIndent,
    type: TProjectType,
  }),
  t.partial({
    linkedProject: nullable(TLinkedProject),
  }),
])

export type Project = t.TypeOf<typeof TProject>

export const TActivity = t.strict({
  actionNumber: t.number,
  date: DateFromISOString,
})

export type Activity = t.TypeOf<typeof TActivity>

export const TProjectLanguageActivityList = t.array(
  t.strict({
    language: t.string,
    activity: t.array(TActivity),
    users: t.array(TUser),
    filledTermsNumber: t.number,
    totalTermsNumber: t.number,
    lastActivityAt: nullable(DateFromISOString),
    addedTranslationsNumber: t.number,
  }),
)

export type ProjectLanguageActivities = t.TypeOf<
  typeof TProjectLanguageActivityList
>

export const TTagsList = t.array(t.string)

export const TChatGPTTranslation = t.strict({
  language: t.string,
  text: t.string,
})

export const TProjectRepo = t.strict({
  repoId: t.string,
  repo: t.string,
  url: t.string,
  username: t.string,
  type: t.union([t.literal('github'), t.literal('gitlab')]),
  baseBranch: t.string,
  createdAt: DateFromISOString,
  pathTemplate: t.string,
  completed: t.boolean,
})

export type ProjectRepo = t.TypeOf<typeof TProjectRepo>

export const TProjectReposList = t.array(TProjectRepo)

export const TProjectUpdates = t.strict({
  addedTermsNumber: t.number,
  addedTranslationNumber: t.strict({
    languages: t.record(t.string, t.number),
    total: t.number,
  }),
  changesNumber: t.number,
  lastViewedAt: nullable(DateFromISOString),
})

export type ProjectUpdates = t.TypeOf<typeof TProjectUpdates>

export const TGptSuggestion = t.strict({
  language: t.string,
  text: t.string,
})

export const TFigmaFile = t.strict({
  fileId: t.string,
  fileName: t.string,
  createdAt: DateFromISOString,
})

export type FigmaFile = t.TypeOf<typeof TFigmaFile>

export const TProjectFigmaDetails = t.strict({
  projectId: t.string,
  name: t.string,
  figma: nullable(TFigmaFile),
})

export type ProjectFigmaDetails = t.TypeOf<typeof TProjectFigmaDetails>

export const TProjectGitHubBranches = t.array(t.string)
